import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import {
  Locale,
  LOCALE_COUNTRY_CODES,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
} from "../../../../shared/constants/locales";
import { LimeSelect, type LimeSelectProps } from "./LimeSelect";
import ReactCountryFlag from "react-country-flag";
import { Optional } from "@/types/utils";

type LocalePicker = Omit<
  Optional<LimeSelectProps, "items">,
  "onChange" | "value"
> & {
  onChange: (value: Locale) => void;
  value?: Locale;
  customOrder?: Locale[];
  /** An option listed before the locales, e.g. "detect automatically". */
  extraOption?: {
    key: string;
    label: string;
    startContent: ReactNode;
    isSelected: boolean;
    onSelect: () => void;
  };
};

export const LimeLocalePicker = ({
  value,
  onChange,
  customOrder,
  extraOption,
  ...props
}: LocalePicker) => {
  const { t } = useLingui();
  const localeItems = SUPPORTED_LOCALES.map((locale) => ({
    label: LOCALE_LABELS[locale],
    key: locale,
    startContent: (
      <ReactCountryFlag countryCode={LOCALE_COUNTRY_CODES[locale]} />
    ),
  })).sort((a, b) => {
    if (!customOrder) return 0;

    const indexA = customOrder.indexOf(a.key);
    const indexB = customOrder.indexOf(b.key);

    // Items not in customOrder go to the end
    const posA = indexA !== -1 ? indexA : Number.MAX_SAFE_INTEGER;
    const posB = indexB !== -1 ? indexB : Number.MAX_SAFE_INTEGER;

    return posA - posB;
  });
  const items = extraOption ? [extraOption, ...localeItems] : localeItems;
  const selected = extraOption?.isSelected ? extraOption.key : value;

  return (
    <LimeSelect
      label={t`Jezik`}
      {...props}
      items={items}
      selectedKeys={selected ? [selected] : undefined}
      disabledKeys={
        props.disabledKeys ? [...props.disabledKeys, "uk", "ru"] : ["uk", "ru"]
      }
      onSingleItemChange={(val) =>
        extraOption && val === extraOption.key
          ? extraOption.onSelect()
          : onChange(val as Locale)
      }
      startContent={
        extraOption?.isSelected ? (
          extraOption.startContent
        ) : value ? (
          <ReactCountryFlag countryCode={LOCALE_COUNTRY_CODES[value]} />
        ) : undefined
      }
      popoverProps={{
        shouldCloseOnInteractOutside: () => true,
      }}
      data-selected-locale={value}
    />
  );
};
